// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import processFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import Check from "@insite/mobius/Icons/Check";
import FileText from "@insite/mobius/Icons/FileText";
import X from "@insite/mobius/Icons/X";
import { Button, GridContainer, Icon, LoadingOverlay, Typography } from "@insite/mobius/index";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import useOrderUpload from "@store/Framework/Common/Hooks/useOrderUpload";
import useProcessFile from "@store/Framework/Common/Hooks/useProcessFile";
import Upload from "@store/Library/Components/Bunzl/Common/Upload";
import ListOrderUploadErrorsModal from "@store/Library/Components/BuyingList/ListOrderUploadErrorsModal";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled, { css } from "styled-components";

interface FileUploadFieldProps {
    onUpload: (products: ProductDto[]) => Promise<void>;
}
const validTypes = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const UploadLinkSpan = styled.span<{ isDragOver: boolean }>`
    color: ${getColor("primary")};
    text-decoration: underline;
    cursor: pointer;
    &:hover {
        color: ${getColor("secondary")};
    }
    ${props =>
        props.isDragOver &&
        css`
            color: ${getColor("secondary")};
        `}
`;

const UploadFieldContainer = styled.div<{ isDragOver: boolean }>`
    width: 28rem;
    height: 15rem;
    background-color: ${getColor("common.checkboxPickerBackground")};
    outline: 2px dashed ${getColor("common.backgroundColorWarmGrey")};
    outline-offset: -10px;
    display: flex;
    gap: 1.125rem;
    position: relative;
    flex-direction: column;
    max-width: 100%;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    transition: outline-offset 0.4s ease-in-out, background-color 0.4s linear;
    ${props =>
        props.isDragOver &&
        css`
            background-color: ${getColor("common.background")};
            outline-offset: -20px;
            outline-color: ${getColor("common.backgroundContrast")};
        `}
`;

const FileUploadField: React.FC<FileUploadFieldProps> = ({ onUpload }) => {
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [isValidType, setIsValidType] = useState(false);
    const [fileName, setFileName] = useState("");
    const { processFile, results, setResults } = useProcessFile();
    const [isDragOver, setIsDragOver] = useState(false);
    const { uploadProducts, products, errors } = useOrderUpload();

    const toaster = useToaster();
    const handleLinkClick = (event: React.MouseEvent<HTMLInputElement>) => {
        event.stopPropagation();
        fileUploadRef.current.click();
    };
    const isMobile = useIsMobile();
    const handleItemClear = () => {
        setFileName("");
        setResults(results => ({
            ...results,
            isUploading: false,
        }));
    };
    const handleFile = (file: File) => {
        if (validTypes.includes(file.type) === false) {
            toaster.addToast({ body: translate("Invalid File Type"), messageType: "danger" });
            return;
        }
        setIsFileLoading(true);
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = async event => {
            const extension = file.name.split(".").pop() || "";
            if (event?.target?.result && typeof event.target.result !== "string") {
                const results = await processFile({
                    data: event.target.result,
                    fileExtension: extension,
                });

                if (results.isUploading) {
                    setFileName(file.name);
                }
                setIsFileLoading(false);
            }
        };
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isValidType) {
            return;
        }
        const files = event.dataTransfer.files;
        setIsDragOver(false);
        if (files && files.length) {
            handleFile(files[0]);
        }
    };
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            handleFile(event.target.files[0]);
        }
    };
    const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
        if (!isDragOver) {
            setIsDragOver(true);
        }
        event.preventDefault();
        event.stopPropagation();
        const type = event.dataTransfer.items[0].type;
        setIsValidType(validTypes.includes(type));
    };
    const handleUpload = async (products: ProductDto[]) => {
        if (!products.length) {
            toaster.addToast({ body: translate("No products found in file"), messageType: "warning" });
            setFileName("");
            handleItemClear();
            return;
        }
        if (products.length) {
            setIsFileLoading(true);
            await onUpload(products);
            setIsFileLoading(false);
            setFileName("");
            toaster.addToast({ body: translate("Upload Successful"), messageType: "success" });
            handleItemClear();
        }
    };
    if (results.isUploading) {
        return (
            <>
                <ListOrderUploadErrorsModal
                    errorsModalIsOpen={errorModalIsOpen}
                    setErrorsModalIsOpen={setErrorModalIsOpen}
                    descriptionText={siteMessage("ListUpload_UploadErrors")}
                    isBadFile={results.isBadFile}
                    onSubmit={() => {
                        setErrorModalIsOpen(false);
                        handleUpload(products);
                    }}
                    onCancel={() => {
                        setErrorModalIsOpen(false);
                    }}
                    products={products}
                    rowErrors={errors}
                    rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                    uploadLimitExceeded={results.uploadLimitExceeded}
                    uploadErrorText={siteMessage("ListUpload_UploadError")}
                    errorReasons={{
                        [UploadError.ConfigurableProduct]: siteMessage("ListUpload_CannotOrderConfigurable"),
                        [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                        [UploadError.InvalidUnit]: translate("Invalid U/M"),
                        [UploadError.NotFound]: siteMessage("Product_NotFound"),
                    }}
                />
                <LoadingOverlay
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    `}
                    loading={isFileLoading}
                >
                    <UploadFieldContainer
                        isDragOver={false}
                        style={{
                            backgroundColor: "white",
                        }}
                    >
                        <Icon
                            src={X}
                            css={css`
                                color: ${getColor("primary")};
                                cursor: pointer;
                                position: absolute;
                                top: 1.5rem;
                                right: 1.5rem;
                                & :hover {
                                    color: ${getColor("secondary")};
                                }
                            `}
                            onClick={handleItemClear}
                            size={54}
                        />

                        <Icon
                            src={FileText as React.FC}
                            css={css`
                                color: ${getColor("secondary")};
                            `}
                            size={isMobile ? 54 : 70}
                        />
                        <Typography
                            css={css`
                                margin: 0 !important;
                            `}
                            variant="h5"
                        >
                            {fileName}
                        </Typography>
                        <Button
                            onClick={async () => {
                                setIsFileLoading(true);

                                const { rowErrors, products } = await uploadProducts(results.uploadedItems);

                                setIsFileLoading(false);
                                if (rowErrors.length > 0) {
                                    setErrorModalIsOpen(true);
                                } else {
                                    handleUpload(products);
                                }
                            }}
                            css={css`
                                font-size: 1rem;
                            `}
                        >
                            {translate("Add Products")}
                        </Button>
                    </UploadFieldContainer>
                </LoadingOverlay>
            </>
        );
    }

    return (
        <LoadingOverlay
            css={css`
                display: flex;
                justify-content: center;
                align-items: center;
            `}
            loading={isFileLoading}
        >
            <UploadFieldContainer
                onClick={handleLinkClick}
                onDrop={handleDrop}
                isDragOver={isDragOver}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={event => {
                    event.preventDefault();
                    setIsDragOver(false);
                }}
            >
                {!isDragOver ? (
                    <>
                        <Icon
                            css={css`
                                pointer-events: none;
                                color: ${isDragOver ? getColor("secondary") : getColor("primary")};
                            `}
                            src={Upload}
                            size={75}
                        />
                        <input
                            type="file"
                            onChange={handleChange}
                            ref={fileUploadRef}
                            style={{ display: "none" }}
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xlsx, .xls"
                        />

                        <Typography variant="h4">
                            {isMobile ? (
                                <>{translate("Upload a File")}</>
                            ) : (
                                <>
                                    <UploadLinkSpan onClick={handleLinkClick} isDragOver={isDragOver}>
                                        {translate("Choose a File")}{" "}
                                    </UploadLinkSpan>
                                    {translate("or drag it here")}
                                </>
                            )}
                        </Typography>
                    </>
                ) : isValidType ? (
                    <>
                        <Icon
                            src={Check}
                            css={css`
                                pointer-events: none;
                                color: ${getColor("secondary")};
                            `}
                            size={100}
                        />
                        <Typography
                            css={css`
                                pointer-events: none;
                            `}
                            variant="h4"
                        >
                            {translate("Drop it!")}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Icon
                            src={X}
                            css={css`
                                pointer-events: none;
                                color: ${getColor("danger.main")};
                            `}
                            size={100}
                        />
                        <Typography
                            css={css`
                                pointer-events: none;
                            `}
                            variant="h4"
                        >
                            {translate("Invalid File Type")}
                        </Typography>
                    </>
                )}
            </UploadFieldContainer>
        </LoadingOverlay>
    );
};

export default FileUploadField;
